/**
 * CampanaNuevaListPage — `/activa` (landing del módulo DEENEX Activa).
 *
 * Lista las campañas REALES del backend (:3005). Antes pintaba una campaña
 * inventada del dataset demo (21.540 contactos) que el cliente veía al entrar,
 * y linkeaba al wizard mock. Ahora cada fila abre el centro de control real.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Rocket, Layers, ArrowRight, Loader2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { activaApi } from "../api";
import ChipEstado from "../components/ChipEstado";
import { useEsEquipoDeenex } from "../hooks/useEsEquipoDeenex";
import { fechaHora, plural } from "../utils/format";
import { detalleDeLaCampana } from "../utils/marcaCampana";
import type { CampanaReal } from "../types.real";


export default function CampanaNuevaListPage() {
  const [campanas, setCampanas] = useState<CampanaReal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Una persona de un cliente ve sus campañas; armarlas es del equipo (ver SoloEquipo.tsx).
  const equipo = useEsEquipoDeenex();

  useEffect(() => {
    (async () => {
      setLoading(true); setError("");
      try {
        // el conteo de no-contactar se fue con su tarjeta (ahora el acceso vive en el sidebar):
        // pedirlo acá era una request por cada visita a la lista para un número que nadie ve.
        const r = await activaApi.campaigns();
        setCampanas(r?.data || []);
      } catch (e: any) {
        setError(e?.message || "No pudimos conectar con el servidor. Probá de nuevo en un rato.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const vacio = !loading && !error && campanas.length === 0;

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Dónde está en el menú (WhatsApp → QR), como en Líneas y Casillas (no el nombre de la app). */}
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">
                WhatsApp · QR
              </span>
              {/* La marca (saberla de un vistazo evita cargarle la base de un cliente a otro) está en el menú, y en el
                  celular en la barra de arriba: acá había un segundo selector, el mismo estado. */}
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2 tracking-tight">Campañas por QR</h1>
            <p className="text-sm text-slate-500 mt-1">
              Salen por tus líneas de WhatsApp y tus casillas de mail, desde tus archivos de pedidos y clientes.
            </p>
          </div>
          {equipo && (
            <Link
              to="/activa/real"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors shadow-sm"
            >
              <Plus size={16} /> Nueva campaña por QR
            </Link>
          )}
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3.5 text-sm text-red-700 flex items-start gap-2">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">No pudimos traer tus campañas</p>
              <p className="text-xs text-red-600 mt-0.5">{error}</p>
            </div>
          </div>
        )}

        {/* Campañas reales */}
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Tus campañas</p>

          {loading ? (
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-10 text-center">
              <Loader2 size={22} className="animate-spin text-violet-500 mx-auto" />
              <p className="text-xs text-slate-400 mt-2.5">Buscando tus campañas…</p>
            </div>
          ) : vacio && !equipo ? (
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-8 text-center text-sm text-slate-500">
              Todavía no hay campañas. Las arma el equipo de Deenex: cuando haya una, la vas a ver acá.
            </div>
          ) : vacio ? (
            /* Sin campañas, el vacío ES la invitación: una sola pieza que explica y arranca el
               flujo. Antes había dos (un hero arriba + este cartel), diciendo lo mismo. */
            <Link
              to="/activa/real"
              className="group block rounded-2xl border-2 border-dashed border-violet-200 bg-gradient-to-br from-violet-50/60 to-white p-8 hover:border-violet-400 transition-colors"
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-violet-600 text-white flex items-center justify-center shadow-sm flex-shrink-0">
                  <Rocket size={26} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-lg font-bold text-slate-900">Despertá tu base con una campaña nueva</p>
                  <p className="text-sm text-slate-500 mt-0.5">
                    Subís dos archivos (pedidos + clientes) y en pocos pasos tenés las campañas listas para enviar.
                  </p>
                </div>
                <ArrowRight size={20} className="text-violet-400 group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </div>
            </Link>
          ) : (
            <div className="rounded-2xl border border-slate-100 bg-white shadow-sm divide-y divide-slate-50">
              {campanas.map((c) => (
                <Link
                  key={c.id}
                  to={`/activa/campanas/${c.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-slate-50/50 transition-colors"
                >
                  {/* Un ícono de «campaña por archivo», no un emoji de marca: era 🍣 (HATSU) para todos los comercios. */}
                  <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center flex-shrink-0"><FileSpreadsheet size={19} aria-hidden="true" /></div>
                  <div className="flex-1 min-w-0">
                    {/* En el celular el nombre envuelve (con el chip de estado al lado, truncado no se leía qué campaña era). */}
                    <p className="text-sm font-bold text-slate-800 line-clamp-2 sm:truncate">{c.nombre}</p>
                    {/* De una campaña programada, lo único que importa es CUÁNDO sale. "Creada el 15
                        de julio" no le sirve a nadie que dejó 183 mensajes agendados; y si venció,
                        hay que decirlo acá: nadie va a entrar a buscar una campaña que cree agendada. */}
                    {/* la vencida se chequea PRIMERO y por su flag, no por el estado: al vencer, el
                        backend la devuelve a "messaged" para sacarla del barrido del scheduler */}
                    {c.programacionVencida && c.programadoPara ? (
                      <p className="text-xs text-amber-600 font-medium">No salió a horario · esperaba el {fechaHora(c.programadoPara)}</p>
                    ) : c.estado === "programada" && c.programadoPara ? (
                      <p className="text-xs text-indigo-600 font-medium">Sale el {fechaHora(c.programadoPara)}</p>
                    ) : null}
                    {/* Todas se llamaban «Reactivación de octubre de 2026» y no había cómo saber cuál era cuál
                        (QA del 08/10). Esta línea las distingue con lo que ya trae cada una: cuándo se creó, con
                        la hora; cuántos clientes tenía el archivo, y a nombre de quién salen. Visible también en
                        el celular: antes los clientes iban en una columna que ahí se escondía. */}
                    <p className="text-xs text-slate-400">{detalleDeLaCampana(c)}</p>
                  </div>
                  <div className="hidden sm:flex items-center gap-5 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-slate-500"><Layers size={14} /> {(c.grupos || []).length} {plural((c.grupos || []).length, "grupo", "grupos")}</span>
                  </div>
                  <ChipEstado estado={c.estado} className="flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
